import { Button, Card, Dropdown, Icon, Menu } from 'antd';
import React, { FC, useState, useEffect } from 'react';
import { AppointmentDetailsDisplayType, AppointmentsDetailsCard } from './AppointmentsDetailsCard';
import { ChatCard } from './ChatCard';
import { ReferralData } from './GetReferralQuery';
import { ReferralDetailsCard } from './ReferralDetailsCard';
import { AuditLogCard } from './AuditLogCard';
import { ReceiverResponseLogCard } from './ReceiverResponseLogCard';
import { useAppContext } from '../../../../hooks';
import { FeatureFlag, hasFeature } from '../../../../helpers';
import config from '../../../../config';

export const ReferralTabCard: FC<{ referral: ReferralData }> = ({ referral }): JSX.Element => {
  const { currentOrganization } = useAppContext();
  const [tabKey, setTabKey] = useState('referral');
  const [isPrinting, setIsPrinting] = useState(false);
  const hasPrintoutPageFlag = hasFeature(currentOrganization?.featureFlags, FeatureFlag.LiquidReferralPrintoutPage);

  const handlePrint = (): void => {
    setIsPrinting(true);
  };

  const handlePrintSummaryPage = (): void => {
    const url = `${config.endpoint}/organizations/${currentOrganization?.id}/referrals/${referral.id}/print`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    const hideIntercom = () => {
      const intercomLauncher = document.getElementById('launcher');
      if (intercomLauncher) {
        intercomLauncher.style.display = 'none';
      }
    };

    const showIntercom = () => {
      const intercomLauncher = document.getElementById('launcher');
      if (intercomLauncher) {
        intercomLauncher.style.display = '';
      }
    };

    if (isPrinting) {
      hideIntercom();
      window.print();
      setIsPrinting(false);
      setTimeout(showIntercom, 1000);
    }

    window.addEventListener('beforeprint', hideIntercom);
    window.addEventListener('afterprint', showIntercom);

    return () => {
      window.removeEventListener('beforeprint', hideIntercom);
      window.removeEventListener('afterprint', showIntercom);
    };
  }, [isPrinting]);

  const printButtonText =
    tabKey === 'referral'
      ? 'Print Referral Details'
      : tabKey === 'appointments'
      ? 'Print Appointment Details'
      : 'Print';

  let adjustedTabList;

  const baseTabList = [
    { key: 'referral', tab: 'Referral' },
    { key: 'appointments', tab: 'Appointments' },
    { key: 'chat', tab: 'Chat' },
    { key: 'auditLogs', tab: 'Referral Status Logs' },
  ];

  // Flag OFF: existing print button as a tab
  const printButtonTab = {
    key: 'print',
    tab: (
      <Button
        onClick={e => {
          e.stopPropagation();
          handlePrint();
        }}
        type="primary"
        icon="printer"
      >
        {printButtonText}
      </Button>
    ),
  };

  // Flag ON: dropdown with both print options
  const printMenu = (
    <Menu>
      <Menu.Item key="print" onClick={handlePrint}>
        <Icon type="printer" /> {printButtonText}
      </Menu.Item>
      <Menu.Item key="printSummaryPage" onClick={handlePrintSummaryPage}>
        <Icon type="printer" /> Print Summary Page
      </Menu.Item>
    </Menu>
  );

  const printDropdownTab = {
    key: 'printDropdown',
    tab: (
      <Dropdown overlay={printMenu} trigger={['click']}>
        <Button type="primary" icon="printer" onClick={e => e.stopPropagation()}>
          Print <Icon type="down" />
        </Button>
      </Dropdown>
    ),
  };

  const isReceiver = currentOrganization?.id !== referral.provider?.organization?.id;
  const hasReceiverResponseFlag = hasFeature(currentOrganization?.featureFlags, FeatureFlag.ReceiverResponseTracking);

  const tabListWithReceiverResponse =
    hasReceiverResponseFlag && isReceiver
      ? [...baseTabList, { key: 'receiverResponseLogs', tab: 'Receiver Response Logs' }]
      : baseTabList;

  // Only show print tab on Referral or Appointments tabs
  const showPrintTab = tabKey === 'referral' || tabKey === 'appointments';
  const printTab = hasPrintoutPageFlag ? printDropdownTab : printButtonTab;
  const fullTabList = showPrintTab ? [...tabListWithReceiverResponse, printTab] : tabListWithReceiverResponse;

  // When printing, only show the active tab
  if (isPrinting) {
    const activeTab = baseTabList.find(tab => tab.key === tabKey);
    adjustedTabList = activeTab ? [activeTab] : baseTabList;
  } else {
    adjustedTabList = fullTabList;
  }

  const tabComponents: {
    [key: string]: JSX.Element;
  } = {
    referral: <ReferralDetailsCard referral={referral} />,
    appointments: (
      <AppointmentsDetailsCard
        referral={referral}
        appointment={referral.appointment}
        cardType={AppointmentDetailsDisplayType.Referral}
      />
    ),
    chat: <ChatCard referral={referral} />,
    auditLogs: <AuditLogCard referral={referral} />,
    receiverResponseLogs: <ReceiverResponseLogCard referral={referral} />,
  };
  return (
    <>
      <div className="referral-card-container">
        <style>{`
        .referral-card-container .ant-tabs-nav-animated .ant-tabs-tab:last-child {
            padding-left: 9%; /* adjust this value to your preference */
        }
    `}</style>

        <Card
          style={{ width: '100%' }}
          tabList={adjustedTabList}
          activeTabKey={tabKey}
          onTabChange={key => {
            // Only allow switching to content tabs, not print tab
            const validKeys = ['referral', 'appointments', 'chat', 'auditLogs', 'receiverResponseLogs'];
            if (validKeys.includes(key)) {
              setTabKey(key);
            }
          }}
        >
          {isPrinting ? (
            <div className="tab-content-active">{tabComponents[tabKey]}</div>
          ) : (
            <div className={`tab-content ${tabKey}`}>{tabComponents[tabKey]}</div>
          )}
        </Card>
      </div>
    </>
  );
};
